import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// styles and images
import Close from "../../assets/close.svg";
import Delete from "../../assets/delete.svg";
import "./AddRateSheet.css";

// store
import { dbDataStore, messageAndErrorStore, triggersStore, userStore } from "../../store/Store";

// components
import InputField from "../../formComponents/InputField";
import FormButton from "../../formComponents/FormButton";
import Loading from "../../components/Loading";

export default function AddTieredRateSheet(props) {
  const JwtToken = userStore((data) => data.JwtToken);
  const currentUser = userStore((data) => data.currentUser);
  const currentCompany = userStore((data) => data.currentCompany);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setTieredRateSheetTrigger = triggersStore(
    (data) => data.setTieredRateSheetTrigger
  );
  const typesData = dbDataStore((data) => data.typesData);
  const companyData = dbDataStore((data) => data.companyData);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)
  

  // state
  let id = 0;
  if (props.id) {
    id = props.id;
  }
  const [counter, setCounter] = useState(0);
  const [typeOptions, setTypeOptions] = useState([]);
  const [methodOptions, setMethodOptions] = useState([]);
  const [low, setLow] = useState([1]);
  const [high, setHigh] = useState([10]);
  const [rates, setRates] = useState([[]]);
  const [tiers, setTiers] = useState(1);
  const [isPending, setIsPending] = useState(false);
  const [dataPending, setDataPending] = useState(false);
  const [oldName, setOldName] = useState("");
  const [customerOptions, setCustomerOptions] = useState([])
  const [rateSheet, setRateSheet] = useState({
    id: 0,
    method_id: 0,
    owner_company_id: currentCompany.id,
    type_id: 0,
    rs_name: "",
    type_name: "",
    method_name: "",
    created_by_id: 0,
    updated_by_id: 0,
    is_deleted: false,
    mileage_bands: 0,
    rs_rates: [],
    tiered_rates: [[]],
    is_tiered: true,
    customer_id: 0,
    is_active: true,
  });
  
  const navigate = useNavigate();
  // other
  const { exists } = useVerifyUnique(
    props.docs,
    "rs_name",
    rateSheet.rs_name,
    oldName
  );
  // get customer options
  useEffect(() => {
    let arr = []
    if (companyData) {
      companyData.forEach(c => {
        arr.push({value: c.id, label: c.company_name})
      })
      setCustomerOptions(arr)
    }
  }, [companyData])

  useEffect(() => {
    let types = [];
    let methods = [];
    if (typesData) {
      setIsPending(false);
      typesData.forEach((o) => {
        if (o.tbl_column === "type_id") {
          types.push({ value: o.id, label: o.type_name });
        } else if (o.tbl_column === "method_id") {
          methods.push({ value: o.id, label: o.type_name });
        }
      });
    } else {
      toggleNewMessage("Trouble Retreiving Data");
    }
    setTypeOptions(types);
    setMethodOptions(methods);
  }, [typesData, toggleNewMessage]);

  // get rate sheet if id exists
  useEffect(() => {
    if (props.id) {
      setDataPending(true);
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers: headers,
        signal: controller.signal,
      };
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/rate-sheet/${props.id}`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/");
              return;
            }
            toggleNewMessage("Trouble Retreiving Data");
            return;
          } else {
            return res.json();
          }
        })
        .then((data) => {
          setRateSheet((current) => {
            return {
              ...current,
              ...data.rate_sheet,
            };
          });
          setOldName(data.rate_sheet.rs_name);
          var band_count;
          var tier_count = data.tiered_rates.length
          if (data.rate_sheet.type_name === "Tier Rates") {
            band_count = data.tiered_rates[0].length;
            let l = [];
            let h = [];
            setCounter(band_count);
            data.tiered_rates[0].forEach((r) => {
              l.push(r.low_band);
              h.push(r.high_band);
            });
            setLow(l);
            setHigh(h);
          }
          let arr = [];
          for (let i = 0; i < tier_count; i++) {
            arr.push([])
            data.tiered_rates[i].forEach((r) => {
              arr[i].push(r.rate);
            });
          }
          setRates(arr);
          setTiers(tier_count)
          setCounter(band_count - 1);
          setDataPending(false);
        })
        .catch((err) => {
          toggleNewMessage("Something went wrong");
          console.log(err)
          return;
        })
        .finally(() => setDataPending(false));
      return () => controller.abort();
    }
  }, [
    props.id,
    JwtToken,
    currentCompany,
    navigate,
    setAuthStatus,
    toggleNewMessage,
    setDataPending,
  ]);
  
  useEscape(
    setTieredRateSheetTrigger,
    [
      setCounter,
      setRateSheet,
      setRates,
      setLow,
      setHigh,
      setCounter,
      props.setDocId,
    ],
    [
      0,
      {
        id: 0,
        method_id: 0,
        owner_company_id: 0,
        type_id: 0,
        rs_name: "",
        type_name: "",
        method_name: "",
        created_by_id: 0,
        updated_by_id: 0,
        is_deleted: false,
        mileage_bands: 0,
        rs_rates: ["0"],
      },
      [],
      [1],
      [10],
      0,
      "",
    ],
    isPending
  );

  // functions
  const handleAdd = () => {
    setLow([...low, low[counter] + 10]);
    setHigh([...high, high[counter] + 10]);
    setCounter(counter + 1);
  };

  const handleTier = (type) => {
    var newR = rates;
    if (type === "Add") {
      setTiers(tiers + 1);
      newR.push([]);
    } else {
      if (tiers > 1) {
        setTiers(tiers - 1);
        newR.pop();
      }
    }
    setRates(newR);
  };

  const handleClose = () => {
    setCounter(1);
    setTieredRateSheetTrigger(false);
    setRateSheet({
      id: 0,
      method_id: 0,
      owner_company_id: 0,
      type_id: 0,
      rs_name: "",
      type_name: "",
      method_name: "",
      created_by_id: 0,
      updated_by_id: 0,
      is_deleted: false,
      mileage_bands: 0,
      rs_rates: ["0"],
    });
    setRates([]);
    setLow([1]);
    setHigh([10]);
    setCounter(0);
    setRates([]);
    props.setDocId("");
  };

  const handleDelete = (e) => {
    setCounter(counter - 1);
    let i = parseInt(e.target.id);
    let arr = rates;
    if (arr[i]) {
      arr.splice(i, 1);
    }
    setRates(arr);
  };

  const handleChange = (e) => {
    let value = e.target.value;
    let name = e.target.name;

    setRateSheet({
      ...rateSheet,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsPending(true);
    if (exists) {
      toggleNewMessage(
        "This Rate Sheet already exists, please use a different name 😅"
      );
      setIsPending(false);
      return;
    } else if (rates.length === 0 && rateSheet.type_name === "Tier Rates") {
      toggleNewMessage("Please enter at least one mileage band 😅");
      setIsPending(false);
      return;
    } else if (rateSheet.method_name === "" || rateSheet.type_name === "") {
      toggleNewMessage("Please make sure and select a hualing method and type😅");
      setIsPending(false);
      return;
    } else if (rates.type_name === "Tier Rates" && rates.length !== counter) {
      toggleNewMessage(
        "Please make sure and fill out all of the rates for each mileage band."
      );
      setIsPending(false);
      return;
    } else if (rates.type_name === "Fixed Rates" && rates.length === 0) {
      toggleNewMessage("Please add a rate for the Fixed Rate");
      setIsPending(false);
      return;
    }
    let method = "POST";
    if (id > 0) {
      method = "PATCH";
    }
    const requestBody = rateSheet;
    requestBody.created_by_id = currentUser.id;
    requestBody.updated_by_id = currentUser.id;
    requestBody.tiered_rates = rates;
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      body: JSON.stringify(requestBody),
      method,
      headers: headers,
    };
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/rate-sheet/${id}`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAuthStatus(false);
            navigate("/");
            return;
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          toggleNewMessage("Something Went Wrong!");
          setIsPending(false);
          return;
        } else {
          setTieredRateSheetTrigger(false);
          setIsPending(false);
          toggleNewMessage(null, "Rate Sheet Added/Modified");
          props.setDocId("");
        }
      })
      .catch((err) => {
        console.log(err);
        toggleNewMessage("Something Went Wrong!");
        setIsPending(false);
        return;
      })
      .finally(() => setIsPending(false));
    return () => controller.abort();
  };
  
  return (!dataPending) ? (
    <div className="popup">
      <div className="tier-ratesheet-popup-inner">
        {!isPending && (
          <div>
            <img
              className="close"
              onClick={handleClose}
              src={Close}
              alt="close img"
            />
          </div>
        )}
        <div className="mb-3 info">
          <h4>Add Tiered Rate Sheet</h4>
          {rateSheet.type_name === "Tier Rates" && (
            <button className="btn btn-secondary add" onClick={handleAdd}>
              Add Mileage Band
            </button>
          )}
          {rateSheet.type_name !== "" && (
            <button
              className="btn btn-secondary add"
              onClick={() => handleTier("Add")}
            >
              Add Tier
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-3 info">
            <InputField
              title={"Name"}
              className={"info-label"}
              type={"text"}
              name={"rs_name"}
              value={rateSheet.rs_name}
              onChange={handleChange}
              placeholder={"Enter Name"}
              required={true}
            />
          <label>
              <span>Customer</span>
              {id === 0 ? <Select
                id="customer"
                options={customerOptions}
                onChange={(option) =>
                  setRateSheet({
                    ...rateSheet,
                    customer_id: option.value,
                  })
                }
                value={customerOptions.filter(c => c.value === rateSheet.customer_id) || ""}
                className="dropdown"
              /> : <Select
                id="customer"
                options={customerOptions}
                onChange={(option) =>
                  setRateSheet({
                    ...rateSheet,
                    customer_id: option.value,
                  })
                }
                value={customerOptions.filter(c => c.value === rateSheet.customer_id) || ""}
                className="dropdown"
                isDisabled
              />}
            </label>

            <label>
              <span>Hauling Method</span>
              <Select
                id="hauling_method"
                options={methodOptions}
                onChange={(option) =>
                  setRateSheet({
                    ...rateSheet,
                    method_id: option.value,
                    method_name: option.label,
                  })
                }
                placeholder={rateSheet.method_name}
                className="dropdown"
              />
            </label>
            <label>
              <span>Hauling Type</span>
              <Select
                id="hauling_type"
                options={typeOptions}
                onChange={(option) => {
                  setRateSheet({
                    ...rateSheet,
                    type_id: option.value,
                    type_name: option.label,
                  });
                  setRates([[]]);
                }}
                placeholder={rateSheet.type_name}
                className="dropdown"
              />
            </label>
          </div>
          {rateSheet.type_name === "Fixed Rates" && (
            <div className="conatiner-fluid text-center">
              <div className="row flex-nowrap">
                {Array.from(Array(tiers)).map((_, idx) => {
                  return (
                    <div key={idx} className="col-2 rate-label">
                      <label>{`Tier ${idx + 1}`}</label>
                      <img
                        src={Delete}
                        className="delete-tier"
                        alt="delete"
                        onClick={() => handleTier("Delete")}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="row flex-nowrap">
                {Array.from(Array(tiers)).map((_, idx) => {
                  return (
                    <div className="col-2" key={idx}>
                      <input
                        name={`fixed_rate${idx}`}
                        type="number"
                        step="0.01"
                        className="mileage-rate"
                        value={rates[idx][0] || ""}
                        onChange={(e) => {
                          let newArr = [...rates];
                          newArr[idx][0] = parseFloat(e.target.value) || 0;
                          setRates(newArr);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {rateSheet.type_name === "Tier Rates" && (
            <div className="container-fluid text-center">
              <div className="row flex-nowrap">
                <div className="col-2 mileage-label">Start</div>
                <div className="col-2 mileage-label">End</div>
                {Array.from(Array(tiers)).map((_, idx) => {
                  return (
                    <div key={idx} className="col-2 rate-label">
                      <label>{`Tier ${idx + 1}`}</label>
                      <img
                        src={Delete}
                        className="delete-tier"
                        alt="delete"
                        onClick={() => handleTier("Delete")}
                      />
                    </div>
                  );
                })}
              </div>
              {Array.from(Array(counter + 1)).map((_, index) => {
                return (
                  <div className="row flex-nowrap" key={index}>
                    <div className="col-2 mileage">{low[index]}</div>
                    <div className="col-2 mileage">{high[index]}</div>
                    {Array.from(Array(tiers)).map((_, idx) => {
                      return (
                        <div className="col-2" key={idx}>
                          <input
                            name={`mileage_rate${idx}`}
                            type="number"
                            step="0.01"
                            className="mileage-rate"
                            value={rates[idx][index] || ""}
                            onChange={(e) => {
                              let newArr = [...rates];
                              newArr[idx][index] =
                                parseFloat(e.target.value) || 0;
                              setRates(newArr);
                            }}
                          />
                        </div>
                      );
                    })}
                    <div className="col-2 mileage">
                      <img
                        id={index}
                        className="delete-band"
                        alt="delete img"
                        src={Delete}
                        onClick={handleDelete}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <FormButton pending={isPending} name={"Submit"} />
        </form>
      </div>
    </div>
  ) : (
    <Loading />
  );
}
